// Laudo avulso — tela unica: dados do documento em cima, editor livre embaixo.
// Salvamento sempre explicito, como nos outros laudos.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Form, Input, DatePicker, Switch, Select, Button, Space, message,
  Typography, Descriptions, Tag, Spin } from 'antd';
import { SaveOutlined, FilePdfOutlined, EyeOutlined, SafetyCertificateOutlined,
  ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import EditorLaudo from '../components/EditorLaudo';

const { Title, Text } = Typography;

export default function LaudoAvulso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sujo, setSujo] = useState(false);
  const editorRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/laudos-avulsos/' + id);
      setDados(r.data);
      form.setFieldsValue({
        titulo: r.data.titulo || '',
        tag: r.data.tag || '',
        equipamento: r.data.equipamento || '',
        status: r.data.status || 'rascunho',
        incluir_capa: !!r.data.incluir_capa,
        data_inspecao: r.data.data_inspecao
          ? dayjs(String(r.data.data_inspecao).substring(0, 10)) : null
      });
    } catch (e) {
      message.error('Erro ao abrir o laudo: ' + (e.response?.data?.erro || e.message));
    } finally { setCarregando(false); }
  }, [id, form]);

  useEffect(() => { carregar(); }, [carregar]);

  // rede de protecao: o salvamento e explicito, entao sair da pagina perde o
  // que foi digitado. O aviso e do navegador — ele nao grava nada, so pergunta.
  useEffect(() => {
    const aviso = (ev) => { if (sujo) { ev.preventDefault(); ev.returnValue = ''; } };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [sujo]);

  const aoCriarEditor = (ed) => {
    editorRef.current = ed;
    ed.on('update', () => setSujo(true));
  };

  const salvar = async () => {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      const corpo = {
        titulo: v.titulo || null,
        tag: v.tag ? String(v.tag).toUpperCase() : null,
        equipamento: v.equipamento || null,
        status: v.status || 'rascunho',
        incluir_capa: !!v.incluir_capa,
        data_inspecao: v.data_inspecao ? v.data_inspecao.format('YYYY-MM-DD') : null,
        conteudo_html: editorRef.current ? editorRef.current.getHTML() : (dados.conteudo_html || '')
      };
      const r = await api.put('/laudos-avulsos/' + id, corpo);
      // so o que o servidor calcula volta para a tela
      setDados((d) => Object.assign({}, d, {
        codigo: r.data.codigo, revisao: r.data.revisao, atualizado_em: r.data.atualizado_em
      }));
      setSujo(false);
      message.success('Laudo salvo!');
      return true;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e.response?.data?.erro || e.message));
      return false;
    } finally { setSalvando(false); }
  };

  const gerarPDF = async () => {
    if (sujo && !window.confirm('Há alterações não salvas. Salvar agora e gerar o PDF?')) return;
    if (sujo && !(await salvar())) return;
    try {
      message.loading({ content: 'Gerando PDF...', key: 'pdfav', duration: 0 });
      try {
        await api.get('/laudos-avulsos/' + id + '/pdf', { responseType: 'blob', timeout: 180000 });
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        message.destroy('pdfav');
        if (!window.confirm('Este laudo já está assinado digitalmente. Gerar um PDF novo APAGA a assinatura (será preciso assinar de novo). Continuar?')) return;
        message.loading({ content: 'Gerando PDF...', key: 'pdfav', duration: 0 });
        await api.get('/laudos-avulsos/' + id + '/pdf?forcar=1', { responseType: 'blob', timeout: 180000 });
      }
      message.success({ content: 'PDF gerado e salvo!', key: 'pdfav' });
      carregar();
    } catch (e) {
      message.error({ content: 'Erro ao gerar PDF: ' + (e.response?.data?.erro || e.message), key: 'pdfav' });
    }
  };

  const verPDF = async () => {
    try {
      message.loading({ content: 'Carregando PDF...', key: 'vizav', duration: 0 });
      const r = await api.get('/laudos-avulsos/' + id + '/pdf/visualizar', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      message.success({ content: 'PDF aberto!', key: 'vizav' });
    } catch (e) {
      message.destroy('vizav');
      message.warning('PDF não gerado ainda. Use o botão Gerar PDF.');
    }
  };

  const assinar = async () => {
    if (!window.confirm('Assinar digitalmente o laudo ' + (dados.codigo || '') + '? Pode levar até 1 minuto.')) return;
    try {
      message.loading({ content: 'Assinando PDF com certificado A1...', key: 'signav', duration: 0 });
      await api.post('/laudos-avulsos/' + id + '/pdf/assinar', {}, { timeout: 120000 });
      message.success({ content: 'PDF assinado com sucesso!', key: 'signav' });
      carregar();
    } catch (e) {
      message.error({ content: 'Erro ao assinar: ' + (e.response?.data?.erro || e.message), key: 'signav' });
    }
  };

  if (carregando) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  if (!dados) return <div style={{ padding: 24 }}><Text type="secondary">Laudo não encontrado.</Text></div>;

  const cliente = dados.razao_faturamento || dados.razao_social || dados.nome_cliente || '-';
  const endereco = dados.endereco_atendimento || '-';

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/laudos')}>Voltar</Button>
        <Title level={4} style={{ margin: 0 }}>
          Laudo avulso {dados.codigo ? '— ' + dados.codigo : '(código ao gerar o PDF)'}
        </Title>
        {dados.pdf_assinado_em && <Tag color="green">assinado digitalmente</Tag>}
        {sujo && <Tag color="orange">alterações não salvas</Tag>}
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Cliente">{cliente}</Descriptions.Item>
          <Descriptions.Item label="CNPJ">{dados.cnpj_faturamento || dados.cnpj || '-'}</Descriptions.Item>
          <Descriptions.Item label="OS">{dados.numero_os || '-'}</Descriptions.Item>
          <Descriptions.Item label="Técnico">{dados.tecnico || '-'}</Descriptions.Item>
          <Descriptions.Item label="Endereço de atendimento" span={2}>{endereco}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form form={form} layout="vertical" onValuesChange={() => setSujo(true)}>
          <Space align="start" wrap size="large">
            <Form.Item name="titulo" label="Título do documento" style={{ width: 360, marginBottom: 0 }}
              rules={[{ required: true, message: 'Dê um título ao laudo' }]}>
              <Input placeholder="Ex.: Parecer Técnico de Avaliação de Reparo" />
            </Form.Item>
            <Form.Item name="data_inspecao" label="Data" style={{ marginBottom: 0 }}
              rules={[{ required: true, message: 'Informe a data' }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="tag" label="TAG (opcional)" style={{ width: 150, marginBottom: 0 }}>
              <Input style={{ textTransform: 'uppercase' }} />
            </Form.Item>
            <Form.Item name="equipamento" label="Equipamento (opcional)" style={{ width: 230, marginBottom: 0 }}>
              <Input />
            </Form.Item>
            <Form.Item name="status" label="Status" style={{ width: 140, marginBottom: 0 }}>
              <Select options={[{ value: 'rascunho', label: 'Rascunho' }, { value: 'concluido', label: 'Concluído' }]} />
            </Form.Item>
            <Form.Item name="incluir_capa" label="Capa" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="com" unCheckedChildren="sem" />
            </Form.Item>
          </Space>
        </Form>
      </Card>

      <Card size="small" title="Conteúdo do laudo"
        extra={<Text type="secondary" style={{ fontSize: 12 }}>
          cabeçalho, rodapé, dados contratuais e assinaturas entram automaticamente no PDF
        </Text>}>
        <EditorLaudo conteudoInicial={dados.conteudo_html} aoCriar={aoCriarEditor} />
      </Card>

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={salvar}>Salvar</Button>
        <Button icon={<FilePdfOutlined />} onClick={gerarPDF}>Gerar PDF</Button>
        <Button icon={<EyeOutlined />} onClick={verPDF}>Ver PDF</Button>
        <Button icon={<SafetyCertificateOutlined />} onClick={assinar}>Assinar com certificado</Button>
      </Space>
    </div>
  );
}
